import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Upload, Download, Search, ChevronLeft, ChevronRight, Pencil, Archive, ArrowRight } from "lucide-react";
import { Tile, Badge } from "../../Primitives";
import LinkFormModal from "./LinkFormModal";
import ReasonModal from "./ReasonModal";
import ImportReport from "./ImportReport";
import { getLinks, addLink, editLink, retireLink, importLinks, downloadFile } from "../../../lib/classificationApi";
import { formatNumber } from "../../../lib/utils";

const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm transition-colors disabled:opacity-40";

function LinkRow({ l, canEdit, onEdit, onRetire }) {
  return (
    <tr data-testid={`link-row-${l.id}`} className="border-t border-border hover:bg-surface-2/40 align-top">
      <td className="px-4 py-2"><div className="font-mono-tabular text-[11px]">{l.legacy_sku}</div><div className="text-[10.5px] text-muted">{l.legacy_name}</div></td>
      <td className="px-1 py-2 text-muted"><ArrowRight size={12} /></td>
      <td className="px-4 py-2"><div className="font-mono-tabular text-[11px]">{l.replacement_sku}</div><div className="text-[10.5px] text-muted">{l.replacement_name}</div></td>
      <td className="px-4 py-2 font-mono-tabular text-[11px] whitespace-nowrap">{l.effective_date}</td>
      <td className="px-4 py-2 text-[11px] capitalize">{l.type}</td>
      <td className="px-4 py-2 font-mono-tabular text-right">{l.ratio}</td>
      <td className="px-4 py-2 text-[10.5px] text-muted capitalize">{l.source}</td>
      <td className="px-4 py-2"><Badge status={l.status === "active" ? "success" : "neutral"}>{l.status}</Badge></td>
      <td className="px-4 py-2 text-right whitespace-nowrap">
        {canEdit && l.status === "active" && (
          <span className="inline-flex gap-1">
            <button data-testid={`link-edit-${l.id}`} onClick={() => onEdit(l)} className="w-7 h-7 inline-flex items-center justify-center border border-border rounded-sm hover:border-primary hover:text-primary"><Pencil size={11} /></button>
            <button data-testid={`link-retire-${l.id}`} onClick={() => onRetire(l)} className="w-7 h-7 inline-flex items-center justify-center border border-border rounded-sm hover:border-critical hover:text-critical"><Archive size={11} /></button>
          </span>
        )}
      </td>
    </tr>
  );
}

// Supersession links: list, add, edit, retire and CSV import.
export default function LinksManager({ canEdit, actor, onChanged }) {
  const [status, setStatus] = useState("active");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null);
  const [report, setReport] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => getLinks({ status, q, page, size: 50 }).then(setData).catch(() => setData(null)), [status, q, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [status, q]);
  const done = (res, msg) => { load(); onChanged(res, msg); };

  return (
    <Tile dense testid="links-manager" title="Part supersession links"
          action={data && <span className="text-[10px] text-subtle font-mono-tabular">{formatNumber(data.counts.active || 0)} active · {formatNumber(data.counts.retired || 0)} retired</span>}>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border">
        <select data-testid="links-status" value={status} onChange={e => setStatus(e.target.value)} className="bg-bg border border-border px-2 py-1.5 rounded-sm text-[11.5px]">
          <option value="active">Active</option><option value="retired">Retired</option><option value="all">All</option>
        </select>
        <div className="relative flex-1 min-w-[180px] max-w-[320px]">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <input data-testid="links-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search legacy or replacement SKU"
                 className="w-full bg-bg border border-border pl-7 pr-3 py-1.5 rounded-sm text-[12px] focus:outline-none focus:border-primary" />
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <button data-testid="links-template" className={btn} onClick={() => downloadFile("/links/template", "supersession-links-template.csv")}><Download size={11} /> Template</button>
          {canEdit && <>
            <button data-testid="links-import" className={btn} onClick={() => fileRef.current?.click()}><Upload size={11} /> Import CSV</button>
            <input ref={fileRef} data-testid="links-import-file" type="file" accept=".csv,text/csv" className="hidden"
                   onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) setModal({ kind: "import", file: f }); }} />
            <button data-testid="links-add" onClick={() => setModal({ kind: "form" })}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold bg-primary text-primary-fg rounded-sm hover:opacity-90"><Plus size={11} /> Add link</button>
          </>}
        </div>
      </div>
      {report && <div className="px-4 pt-3"><ImportReport report={report} kind="links" onClose={() => setReport(null)} /></div>}
      <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 bg-surface z-10">
            <tr className="text-[10px] uppercase tracking-widest text-subtle border-b border-border">
              {["Legacy SKU", "", "Replacement SKU", "Effective", "Type", "Ratio", "Source", "Status", ""].map((h, i) => <th key={i} className="text-left font-semibold px-4 py-2">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data?.links.map(l => <LinkRow key={l.id} l={l} canEdit={canEdit} onEdit={x => setModal({ kind: "form", link: x })} onRetire={x => setModal({ kind: "retire", link: x })} />)}
            {data && data.links.length === 0 && <tr><td colSpan={9} data-testid="links-empty" className="py-8 text-center text-muted text-[12px]">No links match.</td></tr>}
          </tbody>
        </table>
      </div>
      {data && data.pages > 1 && (
        <div className="flex items-center justify-end gap-2 px-4 py-2 border-t border-border text-[11px] text-muted">
          <button data-testid="links-page-prev" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="w-7 h-7 border border-border rounded-sm inline-flex items-center justify-center disabled:opacity-30"><ChevronLeft size={12} /></button>
          <span className="font-mono-tabular" data-testid="links-page">Page {data.page} / {data.pages}</span>
          <button data-testid="links-page-next" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} className="w-7 h-7 border border-border rounded-sm inline-flex items-center justify-center disabled:opacity-30"><ChevronRight size={12} /></button>
        </div>
      )}
      {modal?.kind === "form" && (
        <LinkFormModal link={modal.link} onClose={() => setModal(null)} onSubmit={async ({ reason, ...body }) => {
          const res = modal.link
            ? await editLink(modal.link.id, { replacement_sku: body.replacement_sku, effective_date: body.effective_date, type: body.type, ratio: body.ratio, reason }, actor)
            : await addLink({ ...body, reason }, actor);
          done(res, `${modal.link ? "Edited" : "Added"} link ${body.legacy_sku} → ${body.replacement_sku}`);
        }} />
      )}
      {modal?.kind === "retire" && (
        <ReasonModal title={`Retire link ${modal.link.legacy_sku}`} testid="link-retire-confirm" confirmLabel="Retire link" onClose={() => setModal(null)}
                     impact={<div>{modal.link.legacy_sku} stops merging into {modal.link.replacement_sku}. Its history is classified on its own again.</div>}
                     onConfirm={async (reason) => done(await retireLink(modal.link.id, reason, actor), `Retired link ${modal.link.legacy_sku}`)} />
      )}
      {modal?.kind === "import" && (
        <ReasonModal title="Import supersession links" testid="links-import-confirm" confirmLabel="Import links" onClose={() => setModal(null)}
                     impact={<div>File <span className="font-mono-tabular">{modal.file.name}</span> · valid rows are added as active links; invalid rows are listed in the report. The original file is archived for audit.</div>}
                     onConfirm={async (reason) => {
                       const res = await importLinks(modal.file, reason, actor);
                       setReport(res);
                       if (res.applied) done(res, `Imported ${res.accepted} links`);
                     }} />
      )}
    </Tile>
  );
}
